import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { FileText, CheckCircle2, Loader2 } from 'lucide-react';
import { portugalDistricts, PT_VIEWBOX } from '../../data/portugalDistricts';
import { trackEvent } from '../../lib/track';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const slugify = (name) =>
  name.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, '-');

const useCountUp = (target, active, duration = 1600) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active || !target) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      setValue(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return value;
};

export const MapLeadSection = () => {
  const [mapData, setMapData] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [activeInfo, setActiveInfo] = useState(null); // { id, name } — district whose info is displayed
  const [requestMode, setRequestMode] = useState(false); // when true, next district tap opens modal
  const [selected, setSelected] = useState(null); // district for modal
  const [form, setForm] = useState({ name: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);
  const mapWrapRef = useRef(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/map-data`)
      .then((r) => r.json())
      .then(setMapData)
      .catch(() => setMapData({ total: 0, activeDistricts: 0, districts: {} }));
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setInView(true),
      { threshold: 0.2 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const totalCount = useCountUp(mapData?.total || 0, inView);
  const districtCount = useCountUp(mapData?.activeDistricts || 0, inView);

  const handleMove = useCallback((e) => {
    const rect = mapWrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  }, []);

  const openModal = (district) => {
    setSelected(district);
    setForm({ name: '', email: '' });
    setSubmitted(false);
    setError('');
  };

  const handleDistrictClick = (district) => {
    setActiveInfo(district);
    if (requestMode) {
      openModal(district);
      setRequestMode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/report-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name.trim(), email: form.email.trim(), district: selected.name }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Erro ao processar o pedido.');
      }
      setSubmitted(true);
      trackEvent('report_lead', { district: selected.name });
    } catch (err) {
      setError(err.message || 'Erro ao processar o pedido. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const districts = mapData?.districts || {};
  const hoveredStats = hovered ? districts[hovered.name] : null;
  const activeStats = activeInfo ? districts[activeInfo.name] : null;

  return (
    <section className="valuare-map-section" id="mercado" ref={sectionRef} data-testid="map-lead-section">
      <div className={`valuare-map-inner ${inView ? 'is-visible' : ''}`}>
        <div className="valuare-map-text">
          <div className="valuare-section-label">Dados de Mercado</div>
          <h2 className="valuare-section-title">
            O Mercado Imobiliário,<br /><em>Distrito a Distrito</em>
          </h2>
          <p className="valuare-map-instruction" data-testid="map-instruction">
            {requestMode
              ? 'Selecione agora o seu distrito no mapa para receber o relatório em PDF.'
              : 'Toque no seu distrito no mapa para ver os dados.'}
          </p>
          <div className="valuare-map-stats">
            <div className="valuare-map-stat" data-testid="map-stat-total">
              <div className="valuare-map-stat-num">{totalCount}</div>
              <div className="valuare-map-stat-label">Avaliações Realizadas</div>
            </div>
            <div className="valuare-map-stat" data-testid="map-stat-districts">
              <div className="valuare-map-stat-num">{districtCount}</div>
              <div className="valuare-map-stat-label">Distritos Abrangidos</div>
            </div>
          </div>
          <div className="valuare-map-hint">
            <FileText size={16} strokeWidth={1.5} />
            <span>Relatório gratuito em PDF · valores médios, tipologias e tendências</span>
          </div>
          <p className="valuare-map-sentence" data-testid="map-stats-sentence">
            A Valuare JR já realizou mais de 500 avaliações imobiliárias, em 11 distritos.
          </p>

          {activeInfo && (
            <div className="valuare-map-info-card" data-testid="map-info-card">
              <h3 className="valuare-map-info-name">{activeInfo.name}</h3>
              {activeStats ? (
                <>
                  <div className="valuare-map-info-row">
                    Avaliações Realizadas: <strong>{activeStats.count}</strong>
                  </div>
                  {activeStats.topTipologia && (
                    <div className="valuare-map-info-row">
                      Tipologia Mais Solicitada: <strong>{activeStats.topTipologia}</strong>
                    </div>
                  )}
                  {activeStats.topType && (
                    <div className="valuare-map-info-row">
                      Tipo de Imóvel: <strong>{activeStats.topType}</strong>
                    </div>
                  )}
                </>
              ) : (
                <p className="valuare-map-info-empty">
                  Em expansão neste distrito. Relatório nacional disponível.
                </p>
              )}
            </div>
          )}

          <button
            type="button"
            className={`valuare-map-request-btn ${requestMode ? 'is-active' : ''}`}
            onClick={() => setRequestMode((v) => !v)}
            data-testid="map-request-btn"
            aria-pressed={requestMode}
          >
            <FileText size={14} strokeWidth={1.75} />
            {requestMode ? 'Cancelar Pedido' : 'Pedir Relatório em PDF'}
          </button>
          {requestMode && (
            <p className="valuare-map-request-hint" data-testid="map-request-hint">
              Agora toque no distrito pretendido para receber o relatório.
            </p>
          )}
        </div>

        <div
          className={`valuare-map-visual ${requestMode ? 'request-mode' : ''}`}
          ref={mapWrapRef}
          onMouseMove={handleMove}
        >
          <svg
            viewBox={PT_VIEWBOX}
            className="valuare-map-svg"
            role="img"
            aria-label="Mapa de Portugal Continental por distritos"
            data-testid="portugal-map-svg"
          >
            {portugalDistricts.map((d) => {
              const active = !!districts[d.name];
              const isSelected = activeInfo?.id === d.id;
              return (
                <path
                  key={d.id}
                  d={d.d}
                  className={`valuare-district ${active ? 'is-active' : ''} ${hovered?.id === d.id ? 'is-hovered' : ''} ${isSelected ? 'is-selected' : ''}`}
                  onMouseEnter={() => setHovered(d)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => handleDistrictClick(d)}
                  data-testid={`district-${slugify(d.name)}`}
                  aria-label={d.name}
                />
              );
            })}
          </svg>

          {hovered && (
            <div
              className="valuare-map-tooltip"
              style={{ left: Math.min(tooltipPos.x + 16, 300), top: tooltipPos.y - 10 }}
              data-testid="map-tooltip"
            >
              <div className="valuare-map-tooltip-name">{hovered.name}</div>
              {hoveredStats ? (
                <>
                  <div className="valuare-map-tooltip-row">
                    Avaliações Realizadas: <strong>{hoveredStats.count}</strong>
                  </div>
                  {hoveredStats.topTipologia && (
                    <div className="valuare-map-tooltip-row">
                      Tipologia Mais Solicitada: <strong>{hoveredStats.topTipologia}</strong>
                    </div>
                  )}
                </>
              ) : (
                <div className="valuare-map-tooltip-row">Em expansão · relatório nacional disponível</div>
              )}
              <div className="valuare-map-tooltip-cta">
                {requestMode ? 'Toque para receber o relatório' : 'Toque para ver os dados'}
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="valuare-map-modal" data-testid="report-modal">
          {!submitted ? (
            <>
              <DialogHeader>
                <DialogTitle className="valuare-map-modal-title" data-testid="report-modal-title">
                  Receber relatório de valores médios para {selected?.name}
                </DialogTitle>
                <DialogDescription className="valuare-map-modal-desc">
                  Enviamos o relatório detalhado em PDF diretamente para o seu e-mail.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="valuare-map-modal-form">
                <Input
                  type="text"
                  placeholder="Nome"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  maxLength={100}
                  className="valuare-map-modal-input"
                  data-testid="report-name-input"
                />
                <Input
                  type="email"
                  placeholder="E-mail"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  maxLength={120}
                  className="valuare-map-modal-input"
                  data-testid="report-email-input"
                />
                {error && (
                  <p className="valuare-map-modal-error" data-testid="report-error">{error}</p>
                )}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="valuare-btn-primary valuare-map-modal-btn"
                  data-testid="report-submit-button"
                >
                  {submitting ? (
                    <><Loader2 size={16} className="animate-spin" style={{ marginRight: 8 }} /> A enviar…</>
                  ) : (
                    'Receber Relatório'
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="valuare-map-modal-success" data-testid="report-success-message">
              <CheckCircle2 size={44} strokeWidth={1.25} className="valuare-map-success-icon" />
              <p>
                Obrigado! O relatório detalhado em PDF já foi enviado para o seu e-mail.
                Por favor, verifique a sua caixa de entrada.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
